import { isNil } from 'lodash-es';
import { NavLink } from 'react-router';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import SessionListItemActions from '@/components/sessions/session-list/session-list-item-actions';
import { SessionListItemAlert } from '@/components/sessions/session-list/session-list-item-alert';
import SessionListItemContent from '@/components/sessions/session-list/session-list-item-content';
import { useFriend } from '@/hooks/contacts/use-friends';
import useUserPlatform from '@/hooks/use-user-platform';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import SessionListItemTime from './session-list-item-time';
import SessionListItemTitle from './session-list-item-title';

interface Props {
  session: ConversationSessionsTypeUnion;
  onLinkClick?: () => void;
}

export function SessionListItem({ session, onLinkClick }: Props) {
  const { ID, UserID, IsTop, SessionType } = session;
  const isGroup = SessionType === 'group';
  const key = generateSessionKey(SessionType, ID);

  const { friend } = useFriend(UserID);
  const draft = useConversationSessionStore((s) => s.drafts[key]);
  const { key: currentKey } = useMessageStore();
  const { isDesktop } = useUserPlatform();

  return (
    <SessionListItemActions session={session}>
      <NavLink
        className={({ isActive }) =>
          cn(
            'border-b last-of-type:border-b-0 rounded-box group-hover:*:bg-base-100 peer-[.visible]:[&>div]:bg-base-200',
            { '*:bg-base-100': isDesktop ? key === currentKey : isActive },
          )
        }
        onClick={onLinkClick}
        to={(isGroup ? ROUTES.MESSAGES.GROUPS : ROUTES.MESSAGES.SESSIONS).$buildPath({ params: { id: ID } })}>
        <div className='flex w-full items-stretch gap-4 px-5 py-2 rounded-s transition-colors'>
          <div className='flex items-center'>
            <AvatarWithStatus
              isOnline={!isGroup && friend?.IsOnline}
              className='size-12'
              imageUrl={isGroup ? session.GroupImg : session.HeadImg}
              label={isGroup ? session.GroupName : session.NickName}
              isPin={IsTop}
              size={[48, 48]}
              defaultAvatar={isGroup ? 'group' : 'user'}
            />
          </div>

          <div className='flex flex-auto flex-col items-stretch gap-1'>
            <SessionListItemTitle session={session} />

            <div className='flex w-full flex-1 gap-1 text-body1'>
              <p className='line-clamp-1 w-24 flex-1 break-all text-metal-500'>
                <SessionListItemAlert
                  hasDraft={!isNil(draft)}
                  session={session}
                />

                <SessionListItemContent session={session} />
              </p>
            </div>
          </div>

          <SessionListItemTime session={session} />
        </div>
      </NavLink>
    </SessionListItemActions>
  );
}
